import { useEffect, useState } from "react";

import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { GridColDef, GridNoRowsOverlay, GridRowModel } from "@mui/x-data-grid";
import AddIcon from '@mui/icons-material/Add';
import { format, parseISO } from "date-fns";
import { useLazyQuery } from "@apollo/client";

import { FragmentType, getFragmentData } from "../../../__generated__";
import { PERSON_STUDENTS_QUERY, PERSON_STUDENT_EDGE_FRAGMENT } from "../../../features/graphql/query/person/personQueries";
import { LINK_PERSON_ON_STUDENTS_FRAGMET, } from "../../../features/graphql/query/person/fragments/peopleFragments";
import { PeopleForLink__StudentFragmentFragment, PersonStudentsQuery, PersonStudentsQueryVariables } from "../../../__generated__/graphql";
import SearchBar from "../../components/SearchBar";
import { dateOnlyISO8601 } from "../../../library/date";
// import { TablePaginated, FetchMoreCallbackArgs, SearchBar } from "ui";
import TablePaginated, { FetchMoreCallbackArgs } from "../../Manage/components/TablePaginated/TablePaginated";
import { PersonDataType } from "../../../context/SelectedPersonContext";
import TableRemoveButton from "../../Manage/components/TableRemoveButton";
import { useParams } from "react-router";

export const DEFAULT_PAGE_SIZE = 5;

const TABLE_COLUMNS: GridColDef[] = [
    { field: "firstName", headerName: "First Name", flex: 1, sortable: true },
    { field: "lastName", headerName: "Last Name", flex: 1, sortable: true },
    { field: "dob", headerName: "Date of Birth", flex: 1, sortable: true },
    { field: "location", headerName: "Location", flex: 1, sortable: true }
];



const mapQueryToTable = (edge: FragmentType<typeof PERSON_STUDENT_EDGE_FRAGMENT>): GridRowModel => {
    const { node } = getFragmentData(PERSON_STUDENT_EDGE_FRAGMENT, edge);
    return {
        ...node,
        name: `${node.lastName} ${node.firstName}`,
        dob: format(parseISO(dateOnlyISO8601(node.dob)), 'MM/dd/yyyy'),
        location: node?.location?.name,
        references: node.references
    }
}

export interface StaffTableLinkPreviewProps {
    selectedPerson?: PersonDataType | null
    removeRowId: string | null,
    onLink: (id: string) => void
    onRemove: (id: string) => void
    onDisabled: (reason: 'NOT_ENOUGH_REFERENCES' | 'UNKNOWN') => void
}

const StaffTableLinkPreview = ({
    selectedPerson,
    removeRowId,
    onLink,
    onRemove,
    onDisabled
}: StaffTableLinkPreviewProps) => {
    const [studentEdge, setStudentEdge] = useState<PeopleForLink__StudentFragmentFragment | null | undefined>(null);
    const [pageTableState, setPageTableState] = useState<{ columns: GridColDef[], rows: GridRowModel[] }>({
        columns: [],
        rows: []
    });
    const { pid } = useParams()

    const [fetchRecords, { data, loading, error, fetchMore }] = useLazyQuery<
        PersonStudentsQuery,
        PersonStudentsQueryVariables>(PERSON_STUDENTS_QUERY, {
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'network-only',
            variables: {
                query: '',
                personId: pid,
                first: DEFAULT_PAGE_SIZE,
                after: null
            }
        });

    useEffect(() => {
        // if (selectedPerson) return;

        async function fetchResults() {
            await fetchRecords()
        }

        fetchResults();
    }, [])

    useEffect(() => {
        if (!selectedPerson) return;

        const fragment = getFragmentData(LINK_PERSON_ON_STUDENTS_FRAGMET, selectedPerson.students);
        setStudentEdge(fragment);
        console.log("the selected person: ", selectedPerson)
        // TODO -- Make student edge fragment generic. For now ignore TypeScript error
        setPageTableState(prevState => {
            // @ts-ignore
            const rows = selectedPerson?.students?.edges?.map(mapQueryToTable) ?? [];
            return {
                ...prevState,
                rows
            }
        });
    }, [selectedPerson])

    useEffect(() => {
        if (!onRemove) return;

        setPageTableState(prevState => {
            const columns = [...TABLE_COLUMNS, {
                field: 'remove',
                headerName: 'Remove',
                headerAlign: "center",
                align: 'center',
                sortable: false,
                width: 100,
                renderCell: (params) => {
                    return (
                        <TableRemoveButton
                            id={params.row.id}
                            removing={params.row.id === removeRowId}
                            onRemove={onRemove}
                            onDisabled={() => {
                                if ((params.row.references ?? 0) <= 1) {
                                    return onDisabled("NOT_ENOUGH_REFERENCES");
                                }

                                onDisabled("UNKNOWN")
                            }}
                            disabled={params.row.references <= 1}
                        />
                    );
                }
            } as GridColDef];

            return {
                ...prevState,
                columns
            }
        })
    }, [onRemove]);
    const handleLink = (id: string) => {
        if (!onLink) return;
        onLink(id)
    }
    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        await fetchMore({
            variables: {
                query: variables.query ?? "",
                personId: pid,
                first: variables.first ?? DEFAULT_PAGE_SIZE,
                after: variables.after ?? null
            }
        }).then((res) => {
            const rows = res?.data?.person?.students?.edges?.map(mapQueryToTable) ?? []
            if (rows && rows.length > 0) {
                setPageTableState(prevState => ({
                    ...prevState,
                    rows
                }))
            }
        })
    }
    return (
        <Paper component="div" variant="outlined" >
            <TablePaginated
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => {
                    return (
                        <Box component="div">
                            <SearchBar
                                value={searchQuery}
                                clearSearch={onSearchClear}
                                onChange={onSearch}>
                                <Button
                                    color="secondary"
                                    onClick={x => handleLink(selectedPerson?.id ?? '')}
                                    startIcon={
                                        <AddIcon
                                            style={{
                                                backgroundColor: "#253D93",
                                                borderRadius: "100%",
                                                color: "white",
                                            }}
                                        />
                                    }
                                >
                                    Link Child
                                </Button>
                            </SearchBar>
                        </Box>
                    )
                }}
                loading={loading}
                pageInfo={data?.person.students?.pageInfo}
                totalRows={data?.person.students?.totalCount ?? 0}
                pageSize={5}
                columns={pageTableState.columns}
                rows={data?.person?.students?.edges?.map(mapQueryToTable) ?? []}
                onFetchMore={handleFetchMore}
                slots={{
                    noRowsOverlay: (data?.person.students?.totalCount ?? 0) <= 0
                        ? NoStudentsFoundOverlay
                        : GridNoRowsOverlay
                }}
                disableBoxShadow={true}
            />
        </Paper>
    )
}

export default StaffTableLinkPreview;

function NoStudentsFoundOverlay() {
    return (
        <Stack height="100%" alignItems="center" justifyContent="center" textAlign="center">
            <Typography>
                There are no students assigned to this staff member
                Select "Link Child"" to get started.
            </Typography>
        </Stack>
    )
}